import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useMatchResult } from '../../api/results';
import {
  fetchVerifySeeds,
  fetchMatchDetails,
  requestCloseIntent,
  confirmClose,
  SerializedInstruction,
} from '../../api/matches';
import { formatLamports, normalizeLamports } from '../../utils/format';
import { VerifySeedResponse } from '@solante/shared';
import { shuffleDeck } from '../../game/deck';
import { toast } from 'sonner';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Transaction, PublicKey, TransactionInstruction } from '@solana/web3.js';
import { Buffer } from 'buffer';
import { sendViaRpc } from '../../wallet/sendViaRpc';

// Animated counter component for dramatic number reveals
const AnimatedNumber: React.FC<{ value: number; duration?: number; prefix?: string }> = ({
  value,
  duration = 1.5,
  prefix = '',
}) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);

      // Easing function for dramatic effect
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.floor(eased * value));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);

  return (
    <span>
      {prefix}
      {displayValue.toLocaleString()}
    </span>
  );
};

// Confetti particle for victory celebrations
const Confetti: React.FC = () => {
  const particles = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 2,
    duration: 2 + Math.random() * 2,
    color: ['#fbbf24', '#fcd34d', '#f59e0b', '#fde047', '#00ffff'][Math.floor(Math.random() * 5)],
  }));

  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute h-2 w-2 rounded-full"
          style={{ backgroundColor: p.color, left: `${p.x}%` }}
          initial={{ y: -20, opacity: 1, rotate: 0 }}
          animate={{
            y: '100vh',
            opacity: [1, 1, 0],
            rotate: Math.random() * 720 - 360,
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            ease: 'easeIn',
          }}
        />
      ))}
    </div>
  );
};

export const ResultsPage: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const location = useLocation();
  const { data, isLoading } = useMatchResult(matchId);
  const [proof, setProof] = useState<VerifySeedResponse | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const { publicKey, signTransaction } = useWallet();
  const { connection } = useConnection();
  const queryClient = useQueryClient();
  const walletAddress = publicKey?.toBase58() ?? null;

  // Fetch match details to check if user is the creator
  const { data: matchDetails } = useQuery({
    queryKey: ['match', matchId, 'details'],
    enabled: Boolean(matchId),
    queryFn: () => fetchMatchDetails(matchId!),
    staleTime: 30_000,
  });

  const isCreator = matchDetails?.creator === walletAddress;
  const canReclaimRent =
    isCreator && (matchDetails?.status === 'completed' || matchDetails?.status === 'cancelled');

  // Get win state from location if available (passed from game room)
  const isWinner = location.state?.isWinner ?? true; // Default to winner view for better UX

  useEffect(() => {
    if (isWinner && !isLoading && data) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [isWinner, isLoading, data]);

  const reclaimRentMutation = useMutation({
    mutationFn: async () => {
      if (!publicKey || !matchId || !walletAddress) {
        throw new Error('Wallet not connected');
      }
      if (!connection) {
        throw new Error('Connection unavailable');
      }

      const reclaimToastId = toast.loading('Step 1/3: Preparing rent reclaim…');

      try {
        const intent = await requestCloseIntent(matchId, walletAddress);

        toast.loading('Step 2/3: Approve the transaction in your wallet.', {
          id: reclaimToastId,
        });

        const instructions = intent.instructions.map(
          (instruction: SerializedInstruction) =>
            new TransactionInstruction({
              programId: new PublicKey(instruction.programId),
              keys: instruction.keys.map((key) => ({
                pubkey: new PublicKey(key.pubkey),
                isSigner: key.isSigner,
                isWritable: key.isWritable,
              })),
              data: Buffer.from(instruction.data, 'base64'),
            }),
        );

        const transaction = new Transaction().add(...instructions);
        transaction.feePayer = publicKey;
        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
        transaction.recentBlockhash = blockhash;

        const signature = await sendViaRpc(transaction, connection, { signTransaction });

        toast.loading('Step 3/3: Confirming on-chain…', {
          id: reclaimToastId,
        });

        await connection.confirmTransaction(
          { signature, blockhash, lastValidBlockHeight },
          'confirmed',
        );

        // Notify server to remove match from closeable list
        await confirmClose(matchId, walletAddress, signature);

        toast.success('Rent reclaimed! ~0.004 SOL returned to your wallet.', {
          id: reclaimToastId,
          duration: 5000,
        });

        return signature;
      } catch (caughtError) {
        const normalizedError =
          caughtError instanceof Error
            ? caughtError
            : new Error('Failed to reclaim rent. Please try again.');
        toast.error(normalizedError.message, { id: reclaimToastId });
        throw normalizedError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matches', 'closeable'] });
      queryClient.invalidateQueries({ queryKey: ['match', matchId] });
    },
  });

  const handleVerify = async () => {
    if (!matchId) return;
    try {
      const response = await fetchVerifySeeds(matchId);
      const mismatches = response.shuffleProofs.filter((entry) => {
        const computed = shuffleDeck(entry.serverSeed, entry.clientSeed, entry.nonce);
        return computed.some((card, idx) => card.id !== entry.deck[idx]);
      });
      setProof(response);
      if (mismatches.length === 0) {
        toast.success('Shuffle verified against local recompute.');
      } else {
        toast.error(`Detected ${mismatches.length} mismatched shuffle sequences.`);
      }
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  if (isLoading || !data) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <motion.div
            className="mx-auto mb-4 h-16 w-16 rounded-full border-2 border-arcade-cyan/30"
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          >
            <div className="h-full w-full rounded-full border-t-2 border-arcade-cyan" />
          </motion.div>
          <p className="font-arcade text-lg text-white/60">Loading settlement details...</p>
        </div>
      </div>
    );
  }

  const winnerPayoutBigInt = normalizeLamports(data.settlement.winnerAmount as unknown);
  const winnerPayout = Number(winnerPayoutBigInt);
  const currency = data.settlement.currency;

  return (
    <>
      <AnimatePresence>{showConfetti && <Confetti />}</AnimatePresence>

      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`min-h-[80vh] space-y-8 py-8 ${isWinner ? '' : 'defeat-bg'}`}
      >
        {/* Hero Section - Dramatic outcome reveal */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', bounce: 0.4, duration: 0.8 }}
          className="text-center"
        >
          {isWinner ? (
            <>
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', bounce: 0.5, delay: 0.2 }}
                className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-yellow-400 to-amber-600 shadow-victory-glow"
              >
                <svg
                  className="h-12 w-12 text-white drop-shadow-lg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </motion.div>
              <motion.h1
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="font-display text-3xl font-bold neon-text-gold sm:text-4xl md:text-5xl"
              >
                VICTORY
              </motion.h1>
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mt-2 font-arcade text-xl text-white/80"
              >
                You dominated the arena
              </motion.p>
            </>
          ) : (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', bounce: 0.3, delay: 0.2 }}
                className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-slate-600 to-slate-800 shadow-defeat-glow"
              >
                <svg
                  className="h-12 w-12 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 12h.01M12 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </motion.div>
              <motion.h1
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="font-display text-3xl font-bold text-slate-400 sm:text-4xl md:text-5xl"
              >
                DEFEAT
              </motion.h1>
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mt-2 font-arcade text-xl text-slate-500"
              >
                The chain had other plans
              </motion.p>
            </>
          )}
        </motion.div>

        {/* Payout Card - Big dramatic number */}
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6, type: 'spring' }}
          className={`mx-auto max-w-md rounded-2xl p-4 text-center sm:max-w-lg sm:p-8 ${
            isWinner
              ? 'border-2 border-joker/50 bg-gradient-to-br from-joker/10 to-transparent shadow-joker-glow'
              : 'border border-white/10 bg-white/5'
          }`}
        >
          <p className="text-xs uppercase tracking-[0.3em] text-white/50">
            {isWinner ? 'Winner Payout' : 'Match Pot'}
          </p>
          <motion.div
            initial={{ scale: 0.5 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.8, type: 'spring', bounce: 0.4 }}
            className={`mt-2 font-display text-3xl font-bold sm:text-5xl ${
              isWinner ? 'neon-text-gold' : 'text-slate-400'
            }`}
          >
            <AnimatedNumber
              value={winnerPayout}
              duration={2}
              prefix={currency === 'SOL' ? '' : '$'}
            />
            <span className="ml-2 text-2xl">{currency}</span>
          </motion.div>
          {isWinner && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="victory-shimmer mt-4 h-1 w-full rounded-full"
            />
          )}
        </motion.div>

        {/* Match Details */}
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="mx-auto max-w-4xl"
        >
          <div className="mb-4 text-center">
            <p className="font-mono text-xs text-white/40">Match ID</p>
            <p className="font-arcade text-xs text-white/70 break-all sm:text-sm">{matchId}</p>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {/* Settlement Card */}
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 1.0 }}
              className="card-match p-5"
            >
              <div className="mb-3 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-arcade-cyan/20">
                  <svg
                    className="h-4 w-4 text-arcade-cyan"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-arcade-cyan">
                  Settlement
                </h3>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex justify-between">
                  <span className="text-white/50">Treasury Fee</span>
                  <span className="font-arcade text-white/80">
                    {formatLamports(
                      normalizeLamports(data.settlement.treasuryAmount as unknown),
                      currency,
                    )}
                  </span>
                </li>
                <li className="flex justify-between">
                  <span className="text-white/50">Winner Payout</span>
                  <span className="font-arcade text-joker">
                    {formatLamports(winnerPayoutBigInt, currency)}
                  </span>
                </li>
                <li className="pt-2">
                  <a
                    href={data.explorers.settlementUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-arcade-cyan hover:underline"
                  >
                    View on Explorer
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </a>
                </li>
                {canReclaimRent && (
                  <li className="pt-3 border-t border-white/10 mt-3">
                    <button
                      type="button"
                      onClick={() => reclaimRentMutation.mutate()}
                      disabled={reclaimRentMutation.isPending}
                      className="w-full btn-press rounded-lg border border-joker/50 bg-joker/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-joker transition hover:bg-joker/20 disabled:opacity-50"
                    >
                      {reclaimRentMutation.isPending ? 'Reclaiming…' : 'Reclaim Rent (~0.004 SOL)'}
                    </button>
                  </li>
                )}
              </ul>
            </motion.div>

            {/* Verification Card */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.1 }}
              className="card-match p-5"
            >
              <div className="mb-3 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-arcade-pink/20">
                  <svg
                    className="h-4 w-4 text-arcade-pink"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-arcade-pink">
                  Fairness
                </h3>
              </div>
              <p className="mb-4 text-xs text-white/50">
                Verify shuffle integrity using commit-reveal cryptography
              </p>
              <button
                type="button"
                onClick={handleVerify}
                className="btn-press w-full rounded-lg border border-arcade-pink/50 bg-arcade-pink/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-arcade-pink transition hover:bg-arcade-pink/20"
              >
                Verify Shuffle
              </button>
              <AnimatePresence>
                {proof && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="mt-4 overflow-hidden"
                  >
                    <p className="mb-2 text-xs font-semibold text-consumable">
                      Verified {proof.shuffleProofs.length} proofs
                    </p>
                    <pre className="max-h-24 overflow-auto rounded bg-black/50 p-2 text-[10px] text-white/60">
                      {proof.shuffleProofs.map((s) => `${s.nonce}: ${s.serverSeed}`).join('\n')}
                    </pre>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Replay Integrity Card */}
            <motion.div
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="card-match p-5"
            >
              <div className="mb-3 flex items-center gap-2">
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                    data.replay.verified ? 'bg-consumable/20' : 'bg-danger/20'
                  }`}
                >
                  <svg
                    className={`h-4 w-4 ${data.replay.verified ? 'text-consumable' : 'text-danger'}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={
                        data.replay.verified
                          ? 'M5 13l4 4L19 7'
                          : 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                      }
                    />
                  </svg>
                </div>
                <h3
                  className={`text-sm font-semibold uppercase tracking-wide ${
                    data.replay.verified ? 'text-consumable' : 'text-danger'
                  }`}
                >
                  Replay
                </h3>
              </div>
              <div
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs ${
                  data.replay.verified
                    ? 'bg-consumable/20 text-consumable'
                    : 'bg-danger/20 text-danger'
                }`}
              >
                <span
                  className={`h-2 w-2 rounded-full ${
                    data.replay.verified ? 'bg-consumable' : 'bg-danger'
                  }`}
                />
                {data.replay.verified ? 'Integrity Verified' : 'Discrepancies Found'}
              </div>
              {data.replay.discrepancies.length > 0 && (
                <ul className="mt-3 space-y-1 text-xs text-white/50">
                  {data.replay.discrepancies.map((d, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <span className="text-danger">!</span>
                      {d}
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="pt-8 text-center"
        >
          <a
            href="/matchmaking"
            className="btn-press inline-flex items-center gap-2 rounded-xl border-2 border-arcade-cyan/50 bg-arcade-cyan/10 px-8 py-3 font-display text-sm uppercase tracking-wide text-arcade-cyan transition hover:border-arcade-cyan hover:bg-arcade-cyan/20"
          >
            {isWinner ? 'Defend Your Crown' : 'Seek Revenge'}
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </a>
        </motion.div>
      </motion.section>
    </>
  );
};
